import React, { useState, useCallback, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	Row,
	Col,
	Badge,
	Spinner,
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane,
	Table,
	Input,
} from 'reactstrap';
import { PageTitle } from './PageTitle';
import { t } from '../utils/labels';
import { UserData } from '@food/auth';
import { ReportNotAllowed } from './ReportNotAllowed';
import { InternalCapability } from '../server-types';
import CONFIG from 'static/config';
import {
	CONTEXTS,
	ERRORS,
	generateError,
	modalHandler,
	sentryHandler,
} from '../utils/errors';
import './SessionsReportsUI.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RoleRow {
	INTERNAL_session_count: number;
	INTERNAL_avg_session_duration: number;
	INTERNAL_total_sessions_time: number;
	PRODUCER_session_count: number;
	PRODUCER_avg_session_duration: number;
	PRODUCER_total_sessions_time: number;
	BUYER_session_count: number;
	BUYER_avg_session_duration: number;
	BUYER_total_sessions_time: number;
	BUYER_PL_ITALIA_session_count: number;
	BUYER_PL_ITALIA_avg_session_duration: number;
	BUYER_PL_ITALIA_total_sessions_time: number;
	FOODSERVICE_session_count: number;
	FOODSERVICE_avg_session_duration: number;
	FOODSERVICE_total_sessions_time: number;
	SHOPWORKER_session_count: number;
	SHOPWORKER_avg_session_duration: number;
	SHOPWORKER_total_sessions_time: number;
	SERVICE_session_count: number;
	SERVICE_avg_session_duration: number;
	SERVICE_total_sessions_time: number;
	CONSUMER_session_count: number;
	CONSUMER_avg_session_duration: number;
	CONSUMER_total_sessions_time: number;
	TOTAL_session_count: number;
	TOTAL_avg_session_duration: number;
	TOTAL_total_sessions_time: number;
}

interface ContentRow {
	page: string;
	page_type: string;
	content_id: string;
	content_local_id: string;
	content_name: string;
	session_count: number;
	page_open_count: number;
	avg_page_open_duration: number;
	page_total_open_time: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseCSV<T>(csv: string): T[] {
	const lines = csv
		.trim()
		.split('\n')
		.map((line) => line.replace(/\r$/, ''));
	if (lines.length < 2) return [];
	const headers = splitCSVLine(lines[0]).map((h) => h.trim());
	return lines.slice(1).map((line) => {
		const values = splitCSVLine(line);
		const row: any = {};
		headers.forEach((h, i) => {
			const v = sanitizeCsvText(values[i] ?? '');
			row[h] = isNaN(Number(v)) || v === '' ? v : Number(v);
		});
		return row as T;
	});
}

function splitCSVLine(line: string): string[] {
	const values: string[] = [];
	let current = '';
	let inQuotes = false;

	for (let i = 0; i < line.length; i++) {
		const c = line[i];

		if (c === '"') {
			if (inQuotes && line[i + 1] === '"') {
				current += '"';
				i++;
			} else {
				inQuotes = !inQuotes;
			}
		} else if (c === ',' && !inQuotes) {
			values.push(current);
			current = '';
		} else {
			current += c;
		}
	}

	values.push(current);
	return values;
}

function sanitizeCsvText(value: string): string {
	const trimmed = value.trim();
	if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
		return trimmed.slice(1, -1).replace(/""/g, '"').trim();
	}
	return trimmed.replace(/^"+|"+$/g, '').replace(/""/g, '"').trim();
}

function formatDuration(seconds: number): string {
	if (!seconds || isNaN(seconds)) return '0s';
	const m = Math.floor(seconds / 60);
	const s = Math.round(seconds % 60);
	return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatNumber(n: number): string {
	if (!n || isNaN(n)) return '0';
	return n.toLocaleString('it-IT');
}

function downloadCSV(csvText: string, filename: string) {
	const blob = new Blob([csvText], { type: 'text/csv' });
	const link = document.createElement('a');
	const url = URL.createObjectURL(blob);
	link.href = url;
	link.download = `${filename}.csv`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

function escapeCSVValue(value: any): string {
	const text = String(value ?? '');
	if (/[",\n]/.test(text)) {
		return `"${text.replace(/"/g, '""')}"`;
	}
	return text;
}

function contentRowsToCSV(rows: ContentRow[]): string {
	const headers = [
		'page',
		'page_type',
		'content_id',
		'content_local_id',
		'content_name',
		'session_count',
		'page_open_count',
		'avg_page_open_duration',
		'page_total_open_time',
	];

	const body = rows.map((row) =>
		headers
			.map((header) => escapeCSVValue((row as any)[header]))
			.join(','),
	);

	return [headers.join(','), ...body].join('\n');
}

const ROLE_COLORS: Record<string, string> = {
	INTERNAL: '#6366f1',
	PRODUCER: '#22c55e',
	BUYER: '#f59e0b',
	BUYER_PL_ITALIA: '#f97316',
	FOODSERVICE: '#06b6d4',
	SHOPWORKER: '#ec4899',
	SERVICE: '#8b5cf6',
	CONSUMER: '#14b8a6',
	TOTAL: '#64748b',
};

const ROLE_LABELS: Record<string, string> = {
	INTERNAL: 'Internal',
	PRODUCER: 'Producer',
	BUYER: 'Buyer',
	BUYER_PL_ITALIA: 'Buyer PL Italia',
	FOODSERVICE: 'Food Service',
	SHOPWORKER: 'Shop Worker',
	SERVICE: 'Service',
	CONSUMER: 'Consumer',
	TOTAL: 'Total',
};

const ROLES = [
	'INTERNAL',
	'PRODUCER',
	'BUYER',
	'BUYER_PL_ITALIA',
	'FOODSERVICE',
	'SHOPWORKER',
	'SERVICE',
	'CONSUMER',
];

const DATE_OPTIONS = [
	{ label: 'Questo Mese', value: getDateBefore(1) },
	{ label: 'Ultimo Trimestre', value: getDateBefore(3) },
	{ label: 'Ultimi 6 Mesi', value: getDateBefore(6) },
	{ label: "Quest'anno", value: getDateBefore(12) },
];

function getDateBefore(months: number): string {
	const d = new Date();
	d.setMonth(d.getMonth() - months);
	return d.toISOString().split('T')[0];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard: React.FC<{
	label: string;
	value: string;
	sub?: string;
	color: string;
	icon: string;
}> = ({ label, value, sub, color, icon }) => (
	<div className="sr-stat-card" style={{ borderTopColor: color }}>
		<div className="sr-stat-icon" style={{ background: color + '20', color }}>
			<i className={`mdi mdi-${icon}`} />
		</div>
		<div className="sr-stat-body">
			<div className="sr-stat-value">{value}</div>
			<div className="sr-stat-label">{label}</div>
			{sub && <div className="sr-stat-sub">{sub}</div>}
		</div>
	</div>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface SessionsReportsUIProps {
	requirements: ReadonlyArray<InternalCapability>;
}

type ContentSortKey =
	| 'session_count'
	| 'page_open_count'
	| 'avg_page_open_duration'
	| 'page_total_open_time';

type SortDirection = 'asc' | 'desc';

export const SessionsReportsUI: React.FC<SessionsReportsUIProps> = ({
	requirements,
}) => {
	const [dateFrom, setDateFrom] = useState(DATE_OPTIONS[0].value);
	const [loading, setLoading] = useState(false);
	const [roleData, setRoleData] = useState<RoleRow | null>(null);
	const [contentData, setContentData] = useState<ContentRow[]>([]);
	const [roleCSV, setRoleCSV] = useState<string | null>(null);
	const [contentCSV, setContentCSV] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<'roles' | 'content'>('content');
	const [contentTab, setContentTab] = useState<'chart' | 'table'>('table');
	const [contentNameFilter, setContentNameFilter] = useState('');
	const [contentTypeFilter, setContentTypeFilter] = useState('');
	const [contentSortKey, setContentSortKey] = useState<ContentSortKey>('session_count');
	const [contentSortDirection, setContentSortDirection] = useState<SortDirection>('desc');
	const [hasLoaded, setHasLoaded] = useState(false);

	const toggleContentSort = useCallback((key: ContentSortKey) => {
		if (key === contentSortKey) {
			setContentSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
			return;
		}
		setContentSortKey(key);
		setContentSortDirection('desc');
	}, [contentSortKey]);

	const contentTypes = useMemo(
		() =>
			Array.from(
				new Set(contentData.map((row) => sanitizeCsvText(row.page_type || '')).filter(Boolean)),
			).sort((a, b) => a.localeCompare(b)),
		[contentData],
	);

	const filteredContentData = useMemo(() => {
		const search = contentNameFilter.trim().toLowerCase();
		return contentData
			.filter((row) => {
				const name = sanitizeCsvText(row.content_name || row.page || '').toLowerCase();
				const type = sanitizeCsvText(row.page_type || '').toLowerCase();
				const matchesName = !search || name.includes(search);
				const matchesType = !contentTypeFilter || type === contentTypeFilter.toLowerCase();
				return matchesName && matchesType;
			});
	}, [contentData, contentNameFilter, contentTypeFilter]);

	const sortedContentData = useMemo(() => {
		const factor = contentSortDirection === 'asc' ? 1 : -1;
		return [...filteredContentData].sort(
			(a, b) => ((a as any)[contentSortKey] - (b as any)[contentSortKey]) * factor,
		);
	}, [filteredContentData, contentSortKey, contentSortDirection]);

	const sortIndicator = useCallback(
		(key: ContentSortKey) => {
			if (contentSortKey !== key) return <i className="mdi mdi-swap-vertical sr-sort-icon" />;
			return contentSortDirection === 'asc' ? (
				<i className="mdi mdi-arrow-up sr-sort-icon" />
			) : (
				<i className="mdi mdi-arrow-down sr-sort-icon" />
			);
		},
		[contentSortDirection, contentSortKey],
	);

	const fetchReports = useCallback(
		async (token: string) => {
			setLoading(true);
			try {
				const query = `?dateFrom=${dateFrom}`;
				const headers = {
					Authorization: token,
					'X-Target-Site': 'HUB',
					referer: 'https://hub.italianfood.net',
					'Content-Type': 'text/csv',
				};

				const [roleRes, contentRes] = await Promise.all([
					fetch(CONFIG.BASE_URL + '/api/reports/sessioni-per-ruolo' + query, { method: 'GET', headers }),
					fetch(CONFIG.BASE_URL + '/api/reports/sessioni-per-contenuto' + query, { method: 'GET', headers }),
				]);

				if (!roleRes.ok) throw new Error(`Role report failed: ${await roleRes.text()}`);
				if (!contentRes.ok) throw new Error(`Content report failed: ${await contentRes.text()}`);

				const roleCsvText = await roleRes.text();
				const contentCsvText = await contentRes.text();

				setRoleCSV(roleCsvText);
				setContentCSV(contentCsvText);

				const roleRows = parseCSV<RoleRow>(roleCsvText);
				const contentRows = parseCSV<ContentRow>(contentCsvText);

				setRoleData(roleRows[0] ?? null);
				setContentData(contentRows);
				setHasLoaded(true);
			} catch (e) {
				const error = generateError(ERRORS.GENERATION, CONTEXTS.REPORT, { originalError: e });
				sentryHandler([error]);
				modalHandler([error]);
				console.error(e);
			} finally {
				setLoading(false);
			}
		},
		[dateFrom],
	);

	return (
		<UserData>
			{({ userData }) => {
				if (requirements.some((c) => userData.internalCapabilities.indexOf(c) === -1)) {
					return <ReportNotAllowed />;
				}

				const token = localStorage.getItem('auth_token') ?? '';

				// ── Role bar chart (session counts) ──
				const roleBarSeries = [
					{
						name: 'Sessioni',
						data: ROLES.map((r) => (roleData ? (roleData as any)[`${r}_session_count`] ?? 0 : 0)),
					},
				];
				const roleBarOptions: ApexCharts.ApexOptions = {
					chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
					colors: ROLES.map((r) => ROLE_COLORS[r]),
					plotOptions: {
						bar: {
							borderRadius: 8,
							distributed: true,
							columnWidth: '55%',
							dataLabels: { position: 'top' },
						},
					},
					dataLabels: {
						enabled: true,
						formatter: (v: number) => formatNumber(v),
						offsetY: -20,
						style: { fontSize: '11px', colors: ['#374151'] },
					},
					legend: { show: false },
					xaxis: {
						categories: ROLES.map((r) => ROLE_LABELS[r]),
						labels: { style: { fontSize: '12px' } },
					},
					yaxis: { labels: { formatter: (v) => formatNumber(v) } },
					tooltip: {
						y: {
							formatter: (v: number) => formatNumber(v) + ' sessioni',
						},
					},
					grid: { borderColor: '#e5e7eb' },
					theme: { mode: 'light' },
				};

				// ── Role avg duration radar ──
				const roleDurationSeries = [
					{
						name: 'Durata media (s)',
						data: ROLES.map((r) => (roleData ? Math.round((roleData as any)[`${r}_avg_session_duration`] ?? 0) : 0)),
					},
				];
				const roleDurationOptions: ApexCharts.ApexOptions = {
					chart: { type: 'radar', toolbar: { show: false }, background: 'transparent' },
					colors: ['#6366f1'],
					xaxis: { categories: ROLES.map((r) => ROLE_LABELS[r]) },
					yaxis: { show: false },
					tooltip: { y: { formatter: (v: number) => formatDuration(v) } },
					fill: { opacity: 0.2 },
					stroke: { width: 2 },
					markers: { size: 4 },
					plotOptions: { radar: { polygons: { strokeColors: '#e5e7eb', connectorColors: '#e5e7eb' } } },
				};

				// ── Donut chart for session share ──
				const donutSeries = ROLES.map((r) => (roleData ? (roleData as any)[`${r}_session_count`] ?? 0 : 0));
				const donutOptions: ApexCharts.ApexOptions = {
					chart: { type: 'donut', background: 'transparent' },
					labels: ROLES.map((r) => ROLE_LABELS[r]),
					colors: ROLES.map((r) => ROLE_COLORS[r]),
					legend: { position: 'bottom', fontSize: '12px' },
					dataLabels: { enabled: true, formatter: (val: number) => val.toFixed(1) + '%' },
					plotOptions: { pie: { donut: { size: '65%' } } },
					tooltip: { y: { formatter: (v: number) => formatNumber(v) + ' sessioni' } },
				};

				// ── Content bar chart (top 20 by sessions) ──
				const topContent = [...contentData]
					.sort((a, b) => b.session_count - a.session_count)
					.slice(0, 20);

				const contentBarSeries = [
					{ name: 'Sessioni', data: topContent.map((r) => r.session_count) },
					{ name: 'Aperture pagina', data: topContent.map((r) => r.page_open_count) },
				];
				const contentBarOptions: ApexCharts.ApexOptions = {
					chart: { type: 'bar', toolbar: { show: true }, background: 'transparent', stacked: false },
					colors: ['#6366f1', '#22c55e'],
					plotOptions: { bar: { borderRadius: 4, columnWidth: '70%' } },
					xaxis: {
						categories: topContent.map((r) => r.content_name || r.page || r.content_id || '—'),
						labels: { rotate: -45, style: { fontSize: '10px' }, maxHeight: 100 },
					},
					yaxis: { labels: { formatter: (v) => formatNumber(v) } },
					legend: { position: 'top' },
					tooltip: {
						y: { formatter: (v: number) => formatNumber(v) },
						x: {
							formatter: (_v, opts) => {
								const row = topContent[opts.dataPointIndex];
								return row ? `${row.page_type}: ${row.content_name || row.page}` : '';
							},
						},
					},
					grid: { borderColor: '#e5e7eb' },
					dataLabels: { enabled: false },
				};

				// ── Content avg duration horizontal bar (top 15) ──
				const topByDuration = [...contentData]
					.filter((r) => r.avg_page_open_duration > 0)
					.sort((a, b) => b.avg_page_open_duration - a.avg_page_open_duration)
					.slice(0, 15);

				const durationHBarSeries = [
					{
						name: 'Durata media (s)',
						data: topByDuration.map((r) => ({
							x: sanitizeCsvText(r.content_name || r.page || '—'),
							y: Math.round(r.avg_page_open_duration),
						})),
					},
				];
				const durationHBarOptions: ApexCharts.ApexOptions = {
					chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
					colors: ['#f59e0b'],
					plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: '60%' } },
					xaxis: {
						labels: { formatter: (v) => formatDuration(Number(v)) },
					},
					yaxis: { labels: { style: { fontSize: '11px' } } },
					dataLabels: {
						enabled: true,
						formatter: (v: number) => formatDuration(v),
						style: { fontSize: '11px' },
					},
					tooltip: { y: { formatter: (v: number) => formatDuration(v) } },
					grid: { borderColor: '#e5e7eb' },
				};

				// ── Total stats ──
				const totalSessions = roleData?.TOTAL_session_count ?? 0;
				const avgDuration = roleData?.TOTAL_avg_session_duration ?? 0;
				const totalTime = roleData?.TOTAL_total_sessions_time ?? 0;
				const uniquePages = new Set(contentData.map((r) => r.page)).size;

				return (
					<div className="sr-root">
						<PageTitle
							title="Analytics Sessioni"
							breadcrumbs={[
								{ label: t`home`, path: '/' },
								{ label: t`Reports`, path: '/reports' },
								{ label: 'Analytics Sessioni' },
							]}
						/>

						{/* ── Filter bar ───────────────────────────────── */}
						<Card className="sr-filter-card">
							<CardBody>
								<div className="sr-filter-row">
									<div className="sr-filter-label">
										<i className="mdi mdi-calendar-range" /> Periodo di Analisi
									</div>
									<div className="sr-date-pills">
										{DATE_OPTIONS.map((opt) => (
											<button
												key={opt.value}
												className={`sr-pill ${dateFrom === opt.value ? 'sr-pill--active' : ''}`}
												onClick={() => setDateFrom(opt.value)}
											>
												{opt.label}
											</button>
										))}
									</div>
									<div className="sr-filter-actions">
										<Button
											color="primary"
											className="sr-btn-load"
											disabled={loading}
											onClick={() => fetchReports(token)}
										>
											{loading ? (
												<><Spinner size="sm" /> Caricamento...</>
											) : (
												<><i className="mdi mdi-chart-line" /> Visualizza</>
											)}
										</Button>
									</div>
								</div>
							</CardBody>
						</Card>

						{loading && (
							<div className="sr-loading-overlay">
								<Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
								<p>Analisi dei dati in corso…</p>
							</div>
						)}

						{!loading && hasLoaded && (
							<>
								{/* ── KPI Strip ────────────────────────────── */}
								<div className="sr-kpi-strip">
									<StatCard
										label="Sessioni Totali"
										value={formatNumber(totalSessions)}
										color="#6366f1"
										icon="account-multiple"
									/>
									<StatCard
										label="Durata Media"
										value={formatDuration(avgDuration)}
										color="#22c55e"
										icon="timer-outline"
									/>
									<StatCard
										label="Tempo Totale"
										value={formatDuration(totalTime)}
										color="#f59e0b"
										icon="clock-outline"
									/>
									<StatCard
										label="Pagine Tracciate"
										value={formatNumber(uniquePages)}
										color="#06b6d4"
										icon="file-document-multiple-outline"
									/>
								</div>

								{/* ── Tab nav ──────────────────────────────── */}
								<Nav tabs className="sr-main-tabs">
									<NavItem>
										<NavLink
											className={activeTab === 'content' ? 'active' : ''}
											onClick={() => setActiveTab('content')}
										>
											<i className="mdi mdi-file-chart" /> Sessioni per Contenuto
										</NavLink>
									</NavItem>
									<NavItem>
										<NavLink
											className={activeTab === 'roles' ? 'active' : ''}
											onClick={() => setActiveTab('roles')}
										>
											<i className="mdi mdi-account-group" /> Sessioni per Ruolo
										</NavLink>
									</NavItem>
								</Nav>

								<TabContent activeTab={activeTab}>
									{/* ── ROLES TAB ────────────────────────── */}
									<TabPane tabId="roles">
										<div className="sr-tab-toolbar">
											<div className="sr-sub-tabs">
												<button className="sr-sub-pill active" disabled>
													<i className="mdi mdi-chart-bar" /> Grafici
												</button>
												<button
													className="sr-sub-pill sr-sub-pill--download"
													onClick={() => roleCSV && downloadCSV(roleCSV, 'sessioni-per-ruolo-originale')}
													disabled={!roleCSV}
												>
													<i className="mdi mdi-download" /> CSV originale
												</button>
											</div>
										</div>
										<Row className="sr-chart-row">
											<Col lg={8} className="sr-chart-col">
												<Card className="sr-chart-card">
													<CardHeader>
														<span className="sr-chart-title">
															<i className="mdi mdi-bar-chart" /> Sessioni per Ruolo
														</span>
													</CardHeader>
													<CardBody>
														<ReactApexChart
															type="bar"
															series={roleBarSeries}
															options={roleBarOptions}
															height={320}
														/>
													</CardBody>
												</Card>
											</Col>
											<Col lg={4} className="sr-chart-col">
												<Card className="sr-chart-card">
													<CardHeader>
														<span className="sr-chart-title">
															<i className="mdi mdi-chart-donut" /> Distribuzione
														</span>
													</CardHeader>
													<CardBody>
														<ReactApexChart
															type="donut"
															series={donutSeries}
															options={donutOptions}
															height={320}
														/>
													</CardBody>
												</Card>
											</Col>
										</Row>

										<Row className="sr-chart-row">
											<Col lg={7} className="sr-chart-col">
												<Card className="sr-chart-card">
													<CardHeader>
														<span className="sr-chart-title">
															<i className="mdi mdi-radar" /> Durata Media per Ruolo
														</span>
													</CardHeader>
													<CardBody>
														<ReactApexChart
															type="radar"
															series={roleDurationSeries}
															options={roleDurationOptions}
															height={320}
														/>
													</CardBody>
												</Card>
											</Col>
											<Col lg={5} className="sr-chart-col">
												<Card className="sr-chart-card sr-role-table-card">
													<CardHeader>
														<span className="sr-chart-title">
															<i className="mdi mdi-table" /> Riepilogo per Ruolo
														</span>
													</CardHeader>
													<CardBody className="sr-role-table-body">
														<Table size="sm" borderless className="sr-role-table">
															<thead>
																<tr>
																	<th>Ruolo</th>
																	<th>Sessioni</th>
																	<th>Durata Media</th>
																</tr>
															</thead>
															<tbody>
																{[...ROLES, 'TOTAL'].map((role) => (
																	<tr key={role}>
																		<td>
																			<span
																				className="sr-role-dot"
																				style={{ background: ROLE_COLORS[role] }}
																			/>
																			{ROLE_LABELS[role]}
																		</td>
																		<td className="sr-duration">
																			{formatNumber((roleData as any)?.[`${role}_session_count`] ?? 0)}
																		</td>
																		<td className="sr-duration">
																			{formatDuration((roleData as any)?.[`${role}_avg_session_duration`] ?? 0)}
																		</td>
																	</tr>
																))}
															</tbody>
														</Table>
													</CardBody>
												</Card>
											</Col>
										</Row>
									</TabPane>

									{/* ── CONTENT TAB ──────────────────────── */}
									<TabPane tabId="content">
										<div className="sr-tab-toolbar">
											<div className="sr-sub-tabs">
											<button
												className={`sr-sub-pill ${contentTab === 'table' ? 'active' : ''}`}
												onClick={() => setContentTab('table')}
											>
												<i className="mdi mdi-table-large" /> Tabella Completa
											</button>
											<button
												className={`sr-sub-pill ${contentTab === 'chart' ? 'active' : ''}`}
												onClick={() => setContentTab('chart')}
											>
												<i className="mdi mdi-chart-bar" /> Grafici
											</button>
											<button
												className="sr-sub-pill sr-sub-pill--download"
												onClick={() => contentCSV && downloadCSV(contentCSV, 'sessioni-per-contenuto-originale')}
												disabled={!contentCSV}
											>
												<i className="mdi mdi-download" /> CSV originale
											</button>
											</div>
										</div>

										{contentTab === 'chart' && (
											<>
												<Row className="sr-chart-row">
													<Col xs={12} className="sr-chart-col">
														<Card className="sr-chart-card">
															<CardHeader>
																<span className="sr-chart-title">
																	<i className="mdi mdi-podium" /> Top 20 Contenuti per Sessioni
																</span>
																<span className="sr-chart-sub">
																	{contentData.length} contenuti totali
																</span>
															</CardHeader>
															<CardBody>
																<ReactApexChart
																	type="bar"
																	series={contentBarSeries}
																	options={contentBarOptions}
																	height={380}
																/>
															</CardBody>
														</Card>
													</Col>
												</Row>
												<Row className="sr-chart-row">
													<Col xs={12} className="sr-chart-col">
														<Card className="sr-chart-card">
															<CardHeader>
																<span className="sr-chart-title">
																	<i className="mdi mdi-clock-fast" /> Top 15 per Durata Media
																</span>
															</CardHeader>
															<CardBody>
																<ReactApexChart
																	type="bar"
																	series={durationHBarSeries}
																	options={durationHBarOptions}
																	height={420}
																/>
															</CardBody>
														</Card>
													</Col>
												</Row>
											</>
										)}

										{contentTab === 'table' && (
											<Card className="sr-chart-card">
												<CardHeader>
													<span className="sr-chart-title">
														<i className="mdi mdi-table-search" /> Tutti i Contenuti
													</span>
													<span className="sr-chart-sub">{sortedContentData.length} / {contentData.length} righe</span>
												</CardHeader>
												<CardBody className="sr-table-body">
													<div className="sr-table-filters">
														<div className="sr-table-filter-item">
															<label className="sr-filter-field-label">Nome Contenuto contiene</label>
															<Input
																type="text"
																value={contentNameFilter}
																onChange={(e) => setContentNameFilter(e.target.value)}
																placeholder="Es. Rivista 2026"
															/>
														</div>
														<div className="sr-table-filter-item">
															<label className="sr-filter-field-label">Tipo</label>
															<Input
																type="select"
																value={contentTypeFilter}
																onChange={(e) => setContentTypeFilter(e.target.value)}
															>
																<option value="">Tutti i tipi</option>
																{contentTypes.map((type) => (
																	<option key={type} value={type}>
																		{type}
																	</option>
																))}
															</Input>
														</div>
														<div className="sr-table-filter-actions">
															<Button
																outline
																color="primary"
																size="sm"
																onClick={() =>
																	downloadCSV(
																		contentRowsToCSV(filteredContentData),
																		'sessioni-per-contenuto-filtrato',
																	)
																}
																disabled={!filteredContentData.length}
															>
																CSV filtrato
															</Button>
															<Button
																outline
																color="secondary"
																size="sm"
																onClick={() => {
																	setContentNameFilter('');
																	setContentTypeFilter('');
																}}
															>
																Reset filtri
															</Button>
														</div>
													</div>
													<Table size="sm" bordered responsive className="sr-content-table">
														<thead>
															<tr>
																<th>#</th>
																<th>Nome Contenuto</th>
																<th>Tipo</th>
																<th
																	className="sr-sortable-header"
																	onClick={() => toggleContentSort('session_count')}
																>
																	Sessioni {sortIndicator('session_count')}
																</th>
																<th
																	className="sr-sortable-header"
																	onClick={() => toggleContentSort('page_open_count')}
																>
																	Aperture {sortIndicator('page_open_count')}
																</th>
																<th
																	className="sr-sortable-header"
																	onClick={() => toggleContentSort('avg_page_open_duration')}
																>
																	Durata Media {sortIndicator('avg_page_open_duration')}
																</th>
																<th
																	className="sr-sortable-header"
																	onClick={() => toggleContentSort('page_total_open_time')}
																>
																	Tempo Totale {sortIndicator('page_total_open_time')}
																</th>
															</tr>
														</thead>
														<tbody>
															{sortedContentData.map((row, i) => (
																	<tr key={i}>
																		<td className="sr-row-num">{i + 1}</td>
																		<td className="sr-content-name">
																			{sanitizeCsvText(row.content_name || row.page || '—')}
																		</td>
																		<td>
																			<Badge className="sr-type-badge">
																				{sanitizeCsvText(row.page_type || '—')}
																			</Badge>
																		</td>
																		<td className="sr-num-cell">
																			{formatNumber(row.session_count)}
																		</td>
																		<td className="sr-num-cell">
																			{formatNumber(row.page_open_count)}
																		</td>
																		<td className="sr-num-cell">
																			{formatDuration(row.avg_page_open_duration)}
																		</td>
																		<td className="sr-num-cell">
																			{formatDuration(row.page_total_open_time)}
																		</td>
																	</tr>
																))}
														</tbody>
													</Table>
												</CardBody>
											</Card>
										)}
									</TabPane>
								</TabContent>
							</>
						)}

						{!loading && !hasLoaded && (
							<div className="sr-empty-state">
								<div className="sr-empty-icon">
									<i className="mdi mdi-chart-areaspline" />
								</div>
								<h4>Seleziona un periodo e clicca <em>Visualizza</em></h4>
								<p>I dati di sessione verranno mostrati con grafici interattivi.</p>
							</div>
						)}
					</div>
				);
			}}
		</UserData>
	);
};
